import { Box, Button, Divider, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBookmark } from "@fortawesome/pro-light-svg-icons"
import { faPen as faPenRegular } from "@fortawesome/pro-regular-svg-icons"
import { allocate } from "dinero.js"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../modules/currency/useMoney"
import { toDinero } from "../../../modules/currency/currencyLib"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { MeetingDetailsOrder } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { DrawerSection } from "./DrawerSection"

const DEFAULT_VAT_RATE = 25

const useStyles = makeStyles()((theme) => ({
    linesInner: {
        padding: "0 1rem 1rem",
    },
    divider: {
        marginTop: ".75rem",
        marginBottom: ".75rem",
    },
    deliveryHead: {
        marginBottom: ".5rem",
    },
    deliveryTime: {
        color: theme.palette.tailwind.slate[900],
    },
    deliveryLabel: {
        color: theme.palette.tailwind.slate[500],
    },
    editLink: {
        "&.MuiButton-root": {
            minWidth: "auto",
            padding: ".25rem .5rem",
            color: theme.palette.tailwind.slate[600],
            fontWeight: 600,
            textTransform: "none",
        },
        "&.MuiButton-root:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
            color: theme.palette.tailwind.slate[800],
        },
        "& .MuiButton-startIcon": {
            color: "inherit",
            marginRight: ".5rem",
        },
        "& .MuiButton-startIcon svg": {
            fontSize: ".75rem",
        },
    },
    readLine: {
        padding: ".25rem 0",
    },
    readLineName: {
        color: theme.palette.tailwind.slate[900],
    },
    readLineQty: {
        color: theme.palette.tailwind.slate[400],
    },
    readLinePrice: {
        color: theme.palette.tailwind.slate[900],
        whiteSpace: "nowrap",
    },
    totalRow: {
        marginTop: ".25rem",
    },
    totalLabel: {
        color: theme.palette.tailwind.slate[900],
    },
    totalValue: {
        color: theme.palette.tailwind.slate[900],
    },
    vatLabel: {
        color: theme.palette.tailwind.slate[500],
    },
    vatValue: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    details: MeetingDetailsOrder
    open: boolean
    onToggle: () => void
    onEdit?: (deliveryTime: string) => void
}>

export function OrderLinesReadSection({ details, open, onToggle, onEdit }: Props) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()

    const vatRate = DEFAULT_VAT_RATE
    const linesCount = details.deliveries.reduce((sum, delivery) => sum + delivery.orderLines.length, 0)
    const totalLabel = details.amount ? moneyFactory.format(toDinero(details.amount)) : ""
    // VAT portion of a VAT-inclusive total: split the total into rate : 100 parts and take the rate part.
    const vatLabel = details.amount ? moneyFactory.format(allocate(toDinero(details.amount), [vatRate, 100])[0]) : ""

    return (
        <DrawerSection
            icon={faBookmark}
            titleId="meeting-service-order-drawer-order-lines-title"
            titleFallback="ORDER LINES"
            count={linesCount}
            open={open}
            onToggle={onToggle}
        >
            <Box className={classes.linesInner}>
                {details.deliveries.map((delivery, index) => {
                    const time = dateTimeFactory.formatTime(dateTimeFactory.fromApi(delivery.deliveryTime))
                    return (
                        <Box key={index} marginBottom={index < details.deliveries.length - 1 ? "1rem" : 0}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                className={classes.deliveryHead}
                            >
                                <Stack direction="row" alignItems="baseline" spacing=".5rem">
                                    <Typography component="span" fontWeight={700} className={classes.deliveryTime}>
                                        {time}
                                    </Typography>
                                    <LocalizedStrict
                                        id="meeting-service-order-drawer-delivery-label"
                                        vars={{ index: index + 1 }}
                                    >
                                        <Typography component="span" className={classes.deliveryLabel}>
                                            {`Delivery ${index + 1}`}
                                        </Typography>
                                    </LocalizedStrict>
                                </Stack>
                                {onEdit && (
                                    <LocalizedStrict id="meeting-service-production-view-edit-items-button">
                                        <Button
                                            variant="text"
                                            className={classes.editLink}
                                            startIcon={<FontAwesomeIcon icon={faPenRegular} />}
                                            onClick={() => onEdit(delivery.deliveryTime)}
                                        >
                                            Edit
                                        </Button>
                                    </LocalizedStrict>
                                )}
                            </Stack>

                            <Stack>
                                {delivery.orderLines.map((line, lineIndex) => (
                                    <Stack
                                        key={`${index}-${lineIndex}`}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing="1rem"
                                        className={classes.readLine}
                                    >
                                        <Typography component="span" fontWeight={500} className={classes.readLineName}>
                                            {line.name}
                                            <Typography
                                                component="span"
                                                className={classes.readLineQty}
                                            >{` × ${line.items}`}</Typography>
                                        </Typography>
                                        <Typography component="span" fontWeight={700} className={classes.readLinePrice}>
                                            {line.price ? moneyFactory.format(toDinero(line.price)) : ""}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )
                })}

                <Divider className={classes.divider} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.totalRow}>
                    <LocalizedStrict id="meeting-service-order-drawer-total">
                        <Typography component="span" fontWeight={700} className={classes.totalLabel}>
                            Total
                        </Typography>
                    </LocalizedStrict>
                    <Typography component="span" fontWeight={700} className={classes.totalValue}>
                        {totalLabel}
                    </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <LocalizedStrict id="meeting-service-order-drawer-vat" vars={{ rate: vatRate }}>
                        <Typography component="span" variant="body2" className={classes.vatLabel}>
                            {`Incl. VAT (${vatRate}%)`}
                        </Typography>
                    </LocalizedStrict>
                    <Typography component="span" variant="body2" className={classes.vatValue}>
                        {vatLabel}
                    </Typography>
                </Stack>
            </Box>
        </DrawerSection>
    )
}
